// @ts-check
/** @typedef {import('./items-menu-types').SectionWithTooltipData} SectionWithTooltipData */

import {h} from 'panel';

import {IconSizeType} from 'common/constants';
import {LabelTheme} from 'common/components/items-menu/types';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {getBoardsDashboardSectionItem} from 'common/report/util/boards/presets';

import {mockDashboardsClient, MockDashboardsClient} from '../clients/mock-dashboards-client';

/** @type{SectionWithTooltipData[]} */
export const itemsMenuSections = [
  {
    label: `Loading section`,
    isLoading: true,
    items: [],
  },
  {
    label: `Recently Viewed`,
    items: [
      {
        label: `Top Events`,
        icon: `star-top-events`,
        isSelected: true,
        hasPill: true,
        pillLabel: `Properties`,
      },
      {
        label: `Viewed Report`,
        icon: `event`,
        hasPill: true,
        isPillDisabled: true,
        pillLabel: `Properties`,
        hasCaret: true,
        tooltipData: {
          header: `Viewed report`,
          entityType: `Event`,
          description: `A user views a report`,
          details: [
            {
              name: `Tracked as`,
              value: [`viewed_report`],
            },
            {
              name: `Verified`,
              value: [`by Hello World on 6/12/2017`],
            },
            {
              name: `Tags`,
              value: [`*important*`],
            },
          ],
          metrics: [
            {
              icon: `person`,
              value: `5`,
              tooltip: `Popularity`,
            },
            {
              icon: `fire`,
              value: `12003`,
              tooltip: `Query Volume`,
            },
            {
              icon: `pulse`,
              value: `2100000`,
              tooltip: `Event Volume`,
            },
          ],
        },
      },
      {
        label: `Tagged Events`,
        icon: `tag`,
        value: `Nested Events`,
        subSections: [
          {
            items: [
              {label: `Viewed report`, icon: `event`},
              {label: `Report loaded`, icon: `event`},
              {label: `Funnel query`, icon: `event`},
              {label: `Bookmark loaded`, icon: `event`},
              {label: `Retention query`, icon: `event`},
            ],
          },
        ],
      },
    ],
  },
  {
    label: `Events`,
    items: [
      {
        label: `All Events`,
        icon: `star-top-events`,
        isSelected: true,
        hasCaret: true,
        isDisabled: true,
      },
      {
        label: `Top Events`,
        icon: `star-top-events`,
        isSelected: true,
        isDisabled: true,
      },
      {
        label: `[Collect everything] Clicked cancel edit elements`,
        icon: `event`,
        isDisabled: true,
        tooltip: {
          text: `Disabled because events exceeded quota`,
        },
      },
      {
        label: `Page Load`,
        icon: `event`,
        tooltipData: {
          header: `Page Load`,
          entityType: `Event`,
          description: `Captures NavigationTimingAPI metrics relative to navigationStart once per page load.

            See: https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming
            and https://developers.google.com/web/fundamentals/performance/navigation-and-resource-timing`.replace(
            /\n +/g,
            `\n`,
          ),
          details: [
            {
              name: `Tracked as`,
              value: [`$pageload`],
            },
            {
              name: `Tags`,
              value: [`Conversion`, `Design`, `E Tag`],
            },
            {
              name: `Platforms`,
              value: [`iOS`, `Web`],
            },
          ],
        },
      },
      {
        label: `All: Add to shortlist v3`,
        icon: `event`,
      },
      {
        label: `$autotrack_pageview`,
        icon: `autotrack`,
      },
      {
        label: `Viewed report`,
        icon: null,
      },
    ],
  },
  {
    items: [
      {
        icon: `profile`,
        label: `All People`,
      },
    ],
  },
  {
    label: `Property Types`,
    items: [
      {
        label: `Device Model`,
        icon: `type-text`,
        hasPill: true,
        pillLabel: `Typecast`,
      },
      {
        label: `Device Pixel Ratio (is it retina?)`,
        icon: `type-number`,
      },
      {
        label: `Email`,
        icon: `type-text`,
      },
      {
        label: `Error`,
        icon: `type-boolean`,
      },
      {
        label: `User Ids`,
        icon: `type-list`,
      },
      {
        label: `Created`,
        icon: `type-date`,
      },
      {
        label: `First App Open Date`,
        icon: `type-date`,
        tooltipData: {
          header: `First App Open Date`,
          entityType: `$ae_first_app_open_date`,
          description: `The date the app was first opened on a user's device.`,
        },
      },
    ],
  },
  {
    label: `Section with beta icon`,
    icon: `beta-tag`,
    items: [
      {
        label: `Awesome Feature`,
      },
    ],
  },
  {
    label: `Section with dataset`,
    items: [
      {
        label: `All Events`,
      },
    ],
  },
  {
    label: `Icon side right`,
    items: [
      {
        label: `Awesome Feature`,
        icon: `beta-tag`,
        iconSide: `right`,
      },
      {
        label: `Feature with upsell`,
        icon: `lightning-bolt`,
        iconSide: `right`,
      },
    ],
  },
  {
    label: `Sublabel`,
    items: [
      {
        label: `Feature`,
        subLabel: `Some feature description`,
      },
      {
        label: `Another feature with looooooooooooong description`,
        subLabel: `Loooooooooooooooooooooooooooooooooooooooooooooooooooooong text`,
        wrapSubLabel: true,
      },
      {
        label: `Date property`,
        labelTheme: LabelTheme.Strong,
        subLabel: `Some description of property`,
        hasCaret: true,
        icon: `type-date`,
      },
    ],
  },
  {
    label: `Non-icon items`,
    items: [
      {
        label: `Last 96 hours`,
      },
      {
        label: `Last 30 days`,
      },
    ],
  },
  {
    label: `Uppercase items`,
    items: [
      {
        label: `Events`,
        labelTheme: LabelTheme.Strong,
      },
      {
        label: `People`,
        labelTheme: LabelTheme.Strong,
      },
    ],
  },
  {
    label: `Lots of items`,
    // Add dummy items to trigger pagination
    items: new Array(100).fill(null).map(() => ({
      label: Math.random().toString(),
    })),
    showLimit: 5,
  },
];

export const withReadOnlyItemMenuSections = [
  {
    label: `First section`,
    isLoading: false,
    items: [
      {
        label: `Results`,
      },
    ],
  },
  {
    label: `Second section`,
    isLoading: false,
    items: [
      {
        icon: `search`,
        isReadOnly: true,
        label: `No items match your search`,
        subLabel: `Feedback: search@mixpanel.com`,
      },
    ],
  },
];

export const sectionsWithCustomRenderers = [
  {
    label: `Admin Users`,
    iconRenderer: (item) =>
      h(
        `div.icon`,
        {style: {display: `flex`, margin: `4px 0`}},
        h(`mp-user-icon`, {attrs: {name: item.label, size: IconSizeType.Medium}}),
      ),
    actionsRenderer: () =>
      h(`div.actions`, {style: {width: `20px`, height: `20px`}}, h(`svg-icon`, {attrs: {icon: `eye-on`}})),
    items: [
      {
        label: `Arnold Shortman`,
      },
      {
        label: `Helga Pataki`,
      },
      {
        label: `Gerald Johanssen`,
      },
    ],
  },
];

const dashboardController = new DashboardSelectorController<MockDashboardsClient>({
  projectId: 3,
  bookmarkID: null,
  dashboardsClient: mockDashboardsClient,
  workspaceId: 75,
});

export const sectionsWithHoverActionRenderers = [
  {
    label: `Cool section`,
    items: [
      {
        label: `Regular item`,
      },
      {
        label: `Another regular item`,
      },
      getBoardsDashboardSectionItem({
        label: `Hover over this item`,
        dashboardController,
        name: ``,
        placeholder: `Select a board`,
        quickCreateSaveDashboardCopy: `Save to New Board`,
        recentDashboardIds: [],
        enableNestedDashboardsRestrictions: true,
      }),
    ],
  },
];
